import { validateRedirectPath } from '@helpers';
import { FEATURE_FLAGS } from '@constants';
import { useAuthStore } from '~/store/auth';
import { useApplicationStore } from '~/store/application';
import { useUserStore } from '~/store/user';
import { requestFluidSessionToken } from '~/support/utils/fluid-app-bridge';
import { firstValueFrom } from 'rxjs';
import { resolveHomeRoute } from '~/support/utils';
import { useImpersonationStore } from '~/store/impersonation';
import { useLaunchDarkly } from '~/composables/useLaunchDarkly';

/**
 * Per-user Fluid login: ask the embedding Fluid admin shell for an App Bridge
 * session token and exchange it (with the DRI identifying the store) for OP
 * tokens. Returns false when not embedded, when Fluid hasn't wired the droplet
 * bridge host yet, or when the exchange fails — callers fall back to the
 * legacy owner-level DRI exchange. Skipped while the session-login flag is off so the bridge
 * handshake cannot delay the DRI login.
 */
async function loginViaFluidSession(auth: ReturnType<typeof useAuthStore>, driToken: string): Promise<boolean> {
  if (!useLaunchDarkly().getFlag(FEATURE_FLAGS.FLUID_SESSION_LOGIN_BE, false)) {
    return false;
  }

  const sessionToken = await requestFluidSessionToken();

  if (!sessionToken) {
    return false;
  }

  try {
    await firstValueFrom(auth.exchangeFluidSession(sessionToken, driToken));

    return true;
  } catch (error) {
    console.error('Fluid session exchange failed, falling back to DRI exchange:', error);

    return false;
  }
}

async function handleShopifyLoginToken(
  token: string,
  auth: ReturnType<typeof useAuthStore>,
  route: ReturnType<typeof useRoute>,
  router: ReturnType<typeof useRouter>,
) {
  const { shopify_login: _removed, ...queryWithoutToken } = route.query;
  const basePath = route.path || '/';

  const resolveLoginRedirect = () => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(queryWithoutToken)) {
      if (value != null && value !== '') {
        const values = Array.isArray(value) ? value : [value];
        for (const v of values) {
          if (v != null && v !== '') params.append(key, String(v));
        }
      }
    }
    const rawRedirectPath = params.toString() ? `${basePath}?${params.toString()}` : basePath;

    return validateRedirectPath(rawRedirectPath) ? rawRedirectPath : resolveHomeRoute(useUserStore().roleName);
  };

  try {
    const result = await firstValueFrom(auth.exchangeShopifyLoginToken(token));

    if (result && 'access_token' in result) {
      await firstValueFrom(auth.handlePostLogin(result));
      router.replace({ query: queryWithoutToken });

      return;
    }

    if (result?.requiresLogin) {
      return navigateTo({ path: '/login', query: { redirect: resolveLoginRedirect() } });
    }

    router.replace({ query: queryWithoutToken });
  } catch (error) {
    console.error('Shopify login exchange failed:', error);

    return navigateTo({ path: '/login', query: { redirect: resolveLoginRedirect() } });
  }
}

export default defineNuxtPlugin({
  // The Fluid session-login gate reads a LaunchDarkly flag, so the LD client must be ready first.
  dependsOn: ['launch-darkly'],
  name: 'init',
  async setup() {
    const auth = useAuthStore();
    const application = useApplicationStore();
    const route = useRoute();
    const router = useRouter();

    // Check for DRI token in query params FIRST
    const driToken = route.query.dri as string | undefined;

    if (driToken && !auth.refreshToken) {
      let loggedIn = await loginViaFluidSession(auth, driToken);

      if (!loggedIn) {
        try {
          await firstValueFrom(auth.exchangeDriToken(driToken));
          loggedIn = true;
        } catch (error) {
          console.error('DRI token exchange failed:', error);
          // Continue to normal auth flow
        }
      }

      // Remove dri from URL only after a successful exchange, so a reload can retry a failed one
      if (loggedIn) {
        const { dri, ...query } = route.query;
        router.replace({ query });
      }
    }

    // Check for Shopify login token (after OAuth redirect)
    const shopifyLoginToken = route.query.shopify_login as string | undefined;
    if (shopifyLoginToken) {
      const redirect = await handleShopifyLoginToken(shopifyLoginToken, auth, route, router);
      if (redirect) return redirect as Promise<void>;
    }

    if (auth.authenticated && !auth.accessToken) {
      await auth.refresh();
    }

    // Resolve impersonation before postLogin: the token exists by now (so the call is authenticated and cannot
    // trigger a refresh loop), and postLogin fans out to store/user/notification loading that must run against the
    // impersonated principal. check() never throws, so a failure here cannot block normal login.
    if (auth.authenticated) {
      await useImpersonationStore().check();
    }

    if (auth.authenticated) {
      await firstValueFrom(auth.postLogin());
    }

    application.load();
  },
});
